import { Ban, Check, CirclePause, CirclePlay, FlaskConical, Lock, Pencil, PlugZap, Plus, ScrollText, ShieldQuestion, Trash2 } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ACTION_KINDS } from '@orgus/protocol';
import type { Id, PolicyDecision, PolicyRule, PolicySimulationRequest, PolicySimulationResult } from '@orgus/protocol';
import { Badge, Button, ConfirmDialog, Dialog, EmptyState, ErrorState, IconButton, Input, Panel, REVEAL_GROUP, RelativeTime, RevealSlot, Segmented, Select, Skeleton, Textarea, cn, plural, toUiError, toast } from '@orgus/ui';
import type { Tone, UiError } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import { deleteRule, saveRule, simulate, useApprovals } from './live.ts';
import { DECISION_LABEL, describeMatch, emptyForm, formOf, inputOf, scopeLabel, sortRules, summarizeSimulation } from './policyModel.ts';
import type { RuleForm } from './policyModel.ts';
import { ExpiryPolicyPanel } from './ExpiryPolicyPanel.tsx';

const DECISION_META: Record<PolicyDecision, { tone: Tone; icon: ReactNode }> = {
  allow: { tone: 'passed', icon: <Check /> },
  ask: { tone: 'waiting', icon: <ShieldQuestion /> },
  deny: { tone: 'failed', icon: <Ban /> },
};

/** A rule's decision in a row: icon plus label, plain. Grey for allow; amber and red only for ask and deny. */
const DECISION_INK: Record<PolicyDecision, string> = { allow: 'text-fg-muted [&_svg]:text-fg-subtle', ask: 'font-medium text-status-attention-ink [&_svg]:text-status-attention', deny: 'font-medium text-status-danger-ink [&_svg]:text-status-danger' };

function DecisionBadge({ d }: { d: PolicyDecision }) {
  return (
    <span data-status-pill="" data-variant="plain" className={cn('inline-flex items-center gap-2 text-ui [&_svg]:size-4', DECISION_INK[d])}>
      {DECISION_META[d].icon}
      {DECISION_LABEL[d]}
    </span>
  );
}

function useProjectName() {
  const projects = useOrgus(useShallow((s) => s.projects));
  return useMemo(() => {
    const f = (id: Id) => projects[id]?.name;
    return { name: f, list: Object.values(projects).sort((a, b) => a.name.localeCompare(b.name)) };
  }, [projects]);
}

function PoliciesSkeleton() {
  return (
    <div className="flex flex-col gap-3" role="status" aria-label="Loading policies">
      <Skeleton className="h-6 w-64" />
      <Skeleton className="h-[220px]" />
      <Skeleton className="h-[160px]" />
    </div>
  );
}

/** A simulation's outcome: the totals before and after, and the requests that would change. */
function SimulationResult({ r, projectName }: { r: PolicySimulationResult; projectName: (id: Id) => string | undefined }) {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border-subtle bg-raised p-3" aria-live="polite">
      <p className="text-sm text-fg">{summarizeSimulation(r)}</p>
      {r.total > 0 && (
        <dl className="grid grid-cols-3 gap-2 text-sm">
          {(['allow', 'ask', 'deny'] as const).map((d) => (
            <div key={d} className="flex flex-col gap-0.5">
              <dt className="text-fg-subtle">{DECISION_LABEL[d]}</dt>
              <dd className="num text-fg">
                {r.before[d]} <span className="text-fg-subtle">to</span> <span className={cn(r.after[d] !== r.before[d] && 'font-medium')}>{r.after[d]}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}
      {r.changed.length > 0 && (
        <ul className="flex max-h-40 flex-col divide-y divide-border-subtle overflow-auto rounded-md border border-border-subtle bg-surface" aria-label="Requests that would change">
          {r.changed.slice(0, 20).map((c, i) => (
            <li key={i} className="flex items-center gap-2 px-2.5 py-1.5 text-sm">
              <span className="min-w-0 flex-1 truncate font-mono text-fg" title={c.subject ?? c.title}>
                {c.subject ?? c.title}
              </span>
              {c.projectId && <span className="hidden max-w-[140px] truncate text-fg-subtle sm:inline">{projectName(c.projectId) ?? c.projectId}</span>}
              <span className="shrink-0 text-fg-muted">
                {DECISION_LABEL[c.before]} <span className="text-fg-subtle">to</span> {DECISION_LABEL[c.after]}
              </span>
              <RelativeTime ts={c.at} className="shrink-0 text-fg-subtle" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface RuleEditorProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: PolicyRule | null;
  /** Pre-filled fields for a new rule ("Make it a rule" from an approval). */
  initial?: Partial<RuleForm>;
  /** One line above the form saying where the rule came from. */
  origin?: ReactNode;
  /** Called with the saved rule, before the editor closes. */
  onSaved?: (rule: PolicyRule) => void | Promise<void>;
}

export function RuleEditor({ open, onOpenChange, editing, initial, origin, onSaved }: RuleEditorProps) {
  const { name, list } = useProjectName();
  const [form, setForm] = useState<RuleForm>(emptyForm);
  const [busy, setBusy] = useState<'save' | 'simulate' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sim, setSim] = useState<PolicySimulationResult | null>(null);
  const initialKey = JSON.stringify(initial ?? null);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? formOf(editing) : { ...emptyForm(), ...(initial ?? {}) });
    setError(null);
    setSim(null);
    setBusy(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, initialKey]);

  const set = <K extends keyof RuleForm>(k: K, v: RuleForm[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setSim(null);
  };
  const fail = (e: unknown) => {
    const u = toUiError(e);
    setError(`${u.what} ${u.why ?? ''} ${u.next ?? ''}`.replace(/\s+/g, ' ').trim());
  };

  const runSim = async () => {
    const v = inputOf(form);
    if (!v.ok) return setError(v.error);
    setBusy('simulate');
    setError(null);
    try {
      setSim(await simulate({ rule: { ...v.input, ...(editing ? { id: editing.id } : {}) } }));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };
  const save = async () => {
    const v = inputOf(form);
    if (!v.ok) return setError(v.error);
    setBusy('save');
    setError(null);
    try {
      const rule = await saveRule(v.input, editing?.id);
      toast(editing ? 'Rule updated' : 'Rule added', { tone: 'success', description: `${DECISION_LABEL[rule.decision]}: ${describeMatch(rule.match)}. Applies to the next request.` });
      await onSaved?.(rule);
      onOpenChange(false);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => busy !== 'save' && onOpenChange(o)}
      title={editing ? 'Edit rule' : origin ? 'New rule from an approval' : 'New rule'}
      description={origin ?? 'Rules decide what sessions Orgus started may do without asking. Project rules win over rules for every project.'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" icon={<FlaskConical />} loading={busy === 'simulate'} onClick={() => void runSim()} className="mr-auto">
            Simulate against last week
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy === 'save'}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy === 'save'} onClick={() => void save()}>
            {editing ? 'Save rule' : 'Add rule'}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Select label="Applies to" value={form.scope} onValueChange={(v) => set('scope', v)} options={[{ value: 'org', label: 'Every project' }, ...list.map((p) => ({ value: p.id, label: p.name }))]} />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-fg-muted">Decision</span>
            <Segmented<PolicyDecision>
              label="Decision"
              value={form.decision}
              onChange={(v) => set('decision', v)}
              options={[
                { value: 'allow', label: 'Allow' },
                { value: 'ask', label: 'Ask a person' },
                { value: 'deny', label: 'Deny' },
              ]}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-fg-muted">Matches</span>
          <Segmented<RuleForm['kind']>
            label="Matches"
            value={form.kind}
            onChange={(v) => set('kind', v)}
            options={[
              { value: 'tool', label: 'A tool a session uses' },
              { value: 'action', label: 'An Orgus action' },
            ]}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
          {form.kind === 'tool' ? (
            <Input label="Tool" value={form.tool} onChange={(e) => set('tool', e.target.value)} placeholder="Bash" hint="Bash, Edit, WebFetch, mcp__*" spellCheck={false} className="font-mono" />
          ) : (
            <Select label="Action" value={form.action} placeholder="Pick an action" onValueChange={(v) => set('action', v)} options={ACTION_KINDS.map((k) => ({ value: k, label: k }))} />
          )}
          <Input
            label="Pattern"
            value={form.pattern}
            onChange={(e) => set('pattern', e.target.value)}
            placeholder={form.kind === 'tool' ? 'npm test:*' : 'a command or path'}
            hint="* matches anything; empty matches any use."
            spellCheck={false}
            className="font-mono"
          />
        </div>
        <Input label="Model (optional)" value={form.model} onChange={(e) => set('model', e.target.value)} placeholder="opus*" spellCheck={false} className="font-mono" />
        <Textarea label="Why (shown to the agent and in the Inbox)" rows={2} value={form.reason} onChange={(e) => set('reason', e.target.value)} placeholder="Tests only read and write inside the repo." />
        <button type="submit" hidden />
      </form>
      {sim && (
        <div className="mt-4">
          <SimulationResult r={sim} projectName={name} />
        </div>
      )}
      {error && (
        <p role="alert" className="mt-4 rounded-md bg-failed-soft px-3 py-2 text-sm text-failed">
          {error}
        </p>
      )}
    </Dialog>
  );
}

/**
 * One rule. On a desktop: decision, the match and its reason, scope, hits, then the row actions
 * revealed on hover or focus. Below 768 px it stacks: decision and hits on line one, the full
 * match (wrapping mono) and its reason under it, so nothing is cut. Built-in defaults show no
 * scope and no actions: the group header says they are built in and read only.
 */
function RuleRow({ r, hits, projectName, onEdit, onDelete, onToggle, busy }: { r: PolicyRule; hits: number; projectName: (id: Id) => string | undefined; onEdit?: () => void; onDelete?: () => void; onToggle?: () => void; busy?: boolean }) {
  const off = r.enabled === false;
  const builtIn = r.id.startsWith('default:');
  // Every row keeps the same trailing cells (scope, hits, actions), empty where a built-in has
  // none, so the hit counts of Rules and Built-in defaults share one right-aligned column.
  return (
    <li data-row="" className={cn(REVEAL_GROUP, 'flex min-h-row-2line flex-wrap items-center gap-x-3 gap-y-1 rounded-control px-4 py-2 transition-colors duration-(--duration-fast) hover:bg-hover', off && 'opacity-60')}>
      <span className="order-1 flex shrink-0 items-center gap-2 md:w-32">
        <DecisionBadge d={r.decision} />
      </span>
      <div className="order-last flex min-w-0 basis-full flex-col md:order-2 md:basis-0 md:flex-1">
        <span className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 font-mono text-ui break-words text-fg md:truncate" title={describeMatch(r.match)}>
            {describeMatch(r.match)}
          </span>
          {off && (
            <Badge tone="ended" icon={<CirclePause />} variant="outline" className="shrink-0">
              Off
            </Badge>
          )}
        </span>
        <span className="text-caption text-fg-subtle md:truncate" title={r.reason}>
          {r.reason}
        </span>
      </div>
      <span className="order-3 hidden w-36 shrink-0 truncate text-caption text-fg-subtle md:inline" title={builtIn ? undefined : scopeLabel(r, projectName)} aria-hidden={builtIn || undefined}>
        {builtIn ? null : scopeLabel(r, projectName)}
      </span>
      <span className="num order-2 ml-auto shrink-0 text-right md:w-20 text-caption text-fg-muted md:order-4 md:ml-0" title="Requests this rule decided in the last 7 days">
        {plural(hits, 'hit')}
      </span>
      {builtIn ? (
        <span className="order-3 w-20 shrink-0 md:order-5" aria-hidden />
      ) : (
        <RevealSlot className="order-3 flex w-20 shrink-0 items-center justify-end gap-1 md:order-5">
          <IconButton size="xs" label="Edit rule" icon={<Pencil />} onClick={onEdit} />
          <IconButton size="xs" label={off ? 'Turn the rule on' : 'Turn the rule off'} icon={off ? <CirclePlay /> : <CirclePause />} onClick={onToggle} loading={busy} />
          <IconButton size="xs" label="Delete rule" icon={<Trash2 />} variant="danger" onClick={onDelete} />
        </RevealSlot>
      )}
    </li>
  );
}

/** Settings, Policies: the rules, how often each decided something, and simulation. */
export function PoliciesView() {
  const { phase, policy } = useApprovals();
  const narrow = useMediaQuery(NARROW_QUERY);
  const loadError = useOrgus((s) => s.loadError);
  const { name } = useProjectName();
  const [editor, setEditor] = useState<{ open: boolean; rule: PolicyRule | null }>({ open: false, rule: null });
  const [removing, setRemoving] = useState<PolicyRule | null>(null);
  const [removeSim, setRemoveSim] = useState<PolicySimulationResult | UiError | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  const rules = useMemo(() => sortRules(policy?.rules ?? [], name), [policy?.rules, name]);
  const hitsOf = (r: PolicyRule) => policy?.hits?.[r.id] ?? r.hits ?? 0;

  useEffect(() => {
    if (!removing) return;
    setRemoveSim(null);
    const req: PolicySimulationRequest = { without: [removing.id] };
    simulate(req).then(setRemoveSim, (e) => setRemoveSim(toUiError(e)));
  }, [removing]);

  if (phase === 'loading') return <PoliciesSkeleton />;
  if (phase === 'error') return <ErrorState error={loadError ?? new Error('The hub could not be reached.')} />;
  if (phase === 'missing' || !policy) {
    return (
      <EmptyState
        icon={<PlugZap />}
        title="Policies are not available on this hub"
        description="Restart the hub on the current Orgus version."
      />
    );
  }

  const toggle = async (r: PolicyRule) => {
    setToggling(r.id);
    try {
      await saveRule({ scope: r.scope, match: r.match, decision: r.decision, reason: r.reason, enabled: r.enabled === false }, r.id);
    } catch (e) {
      const u = toUiError(e);
      toast(`Could not change the rule: ${u.what}`, { tone: 'error', description: u.next });
    } finally {
      setToggling(null);
    }
  };

  return (
    <div className="flex max-w-[960px] flex-col gap-6">
      <ExpiryPolicyPanel />
      <Panel
        title="Rules"
        sub={narrow ? rules.length : `${plural(rules.length, 'rule')}, version ${policy.version}`}
        icon={<ScrollText />}
        flush
        actions={
          <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setEditor({ open: true, rule: null })}>
            New rule
          </Button>
        }
      >
        {rules.length === 0 ? (
          <EmptyState
            icon={<ScrollText />}
            title="No rules yet"
            description="Add one, or answer an approval with Make it a rule."
            action={
              <Button size="sm" variant="secondary" icon={<Plus />} onClick={() => setEditor({ open: true, rule: null })}>
                New rule
              </Button>
            }
          />
        ) : (
          <ul className="flex flex-col px-1 pb-1" aria-label="Rules, in the order they apply">
            {rules.map((r) => (
              <RuleRow key={r.id} r={r} hits={hitsOf(r)} projectName={name} busy={toggling === r.id} onEdit={() => setEditor({ open: true, rule: r })} onDelete={() => setRemoving(r)} onToggle={() => void toggle(r)} />
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Built-in defaults" sub={narrow ? (policy.defaults ?? []).length : 'Read only, applied last'} icon={<Lock />} flush>
        <ul className="flex flex-col px-1 pb-1" aria-label="Built-in defaults">
          {(policy.defaults ?? []).map((r) => (
            <RuleRow key={r.id} r={r} hits={hitsOf(r)} projectName={name} />
          ))}
        </ul>
      </Panel>

      <p className="text-sm text-fg-muted">
        Rules only apply to sessions Orgus started. Sessions Orgus only watches are never asked or blocked. Every change is versioned and shows in Audit
        {policy.updatedAt ? (
          <>
            ; last changed <RelativeTime ts={policy.updatedAt} />
          </>
        ) : null}
        .
      </p>

      <RuleEditor open={editor.open} editing={editor.rule} onOpenChange={(o) => setEditor((e) => ({ ...e, open: o }))} />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Delete this rule?"
        description={removing ? `${DECISION_LABEL[removing.decision]}: ${describeMatch(removing.match)} (${scopeLabel(removing, name)}).` : ''}
        confirmLabel="Delete rule"
        onConfirm={async () => {
          if (removing) await deleteRule(removing.id);
          toast('Rule deleted', { tone: 'neutral', description: 'The next request is decided without it.' });
        }}
      >
        {removeSim == null ? (
          <p className="flex items-center gap-2 text-sm text-fg-muted">
            <FlaskConical className="size-3.5" aria-hidden /> Checking the last 7 days...
          </p>
        ) : 'what' in removeSim ? (
          <p className="text-sm text-fg-muted">Could not simulate: {removeSim.what}</p>
        ) : (
          <SimulationResult r={removeSim} projectName={name} />
        )}
      </ConfirmDialog>
    </div>
  );
}
